function changeActive(evt: Event) {
    const btns: NodeListOf<HTMLButtonElement> =
        document.querySelectorAll('.js-select-btn');
    if (btns) {
        btns.forEach(btn => {
            btn.classList.remove('is-active');
        });
    }
    const currentTarget = evt.target;
    if (currentTarget instanceof HTMLElement) {
        currentTarget.classList.add('is-active');
    }
}

// Search Platform
type SearchResult = {
    document_id: string;
    source_type: string;
    title: string;
    summary?: string;
    body?: string;
    url: string;
    thumbnail_url?: string;
    categories?: string[];
    tags?: string[];
    score?: number;
};

type SearchResponse = {
    query: string;
    site_id: string;
    search_id: string | null;
    suggestions: string[];
    total: number;
    elapsed_ms: number;
    results: SearchResult[];
};

type SearchPageMeta = {
    PAGE_TITLE: string;
    PAGE_DESCRIPTION: string;
    PAGE_KEYWORD: string;
    PAGE_CANONICAL: string;
    PAGE_PERMALINK: string;
};

const SEARCH_API_ENDPOINT = '/search-api/';
const SEARCH_CLICK_API_ENDPOINT = '/search-api/click';
const SEARCH_SITE_ID = 'konote';
const SEARCH_DEFAULT_THUMBNAIL_URL =
    '/common/images/dummy.png';
type GtagFunction = (
    command: 'event',
    eventName: string,
    parameters?: Record<string, string | number>
) => void;
type WindowWithGtag = Window & {
    gtag?: GtagFunction;
};

// Search Platform 表示設定
const showSummary = false;
const showBody = true;
const showCategories = false;
const showTags = false;
const showSearchStats = true;
const showSourceType = false
const showScore = false;
const showDocumentId = false;
const summaryMaxLength = 80;
const bodyMaxLength = 80;
const truncationSuffix = '...';

// ライブ検索設定
const searchDebounceMs = 400;
let searchDebounceTimer: number | undefined;

// 無限スクロール設定
const searchResultLimit = 20;
const infiniteScrollThreshold = 300;
const searchLoadingMinimumMs = 600;
let activeSearchRequestId = 0;
let activeSearchAbortController: AbortController | null = null;
let infiniteScrollObserver: IntersectionObserver | null = null;
let currentSearchQuery = '';
let currentSearchId: string | null = null;
let currentSearchOffset = 0;
let currentSearchTotal = 0;
let isLoadingMore = false;

const containsPotentialPersonalData = (
    query: string
): boolean => {
    const hasEmail =
        /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.test(query);
    const digitCount =
        query.replace(/[^0-9０-９]/g, '').length;
    return hasEmail || digitCount >= 10;
};

const sendSearchAnalyticsEvent = (
    eventName: string,
    query: string,
    parameters: Record<string, string | number>
): void => {
    if (
        !query ||
        containsPotentialPersonalData(query)
    ) {
        return;
    }
    const gtag =
        (window as WindowWithGtag).gtag;

    if (typeof gtag !== 'function') {
        return;
    }
    gtag(
        'event',
        eventName,
        {
            search_term: query,
            site_id: SEARCH_SITE_ID,
            ...parameters,
        }
    );
};

const trackSearchResultsLoaded = (
    data: SearchResponse
): void => {
    sendSearchAnalyticsEvent(
        'search_results_loaded',
        data.query,
        {
            result_count: data.total,
            zero_results:
                data.total === 0 ? 'true' : 'false',
        }
    );
};

const sendSearchPlatformClick = (
    searchId: string,
    documentId: string,
    position: number
): void => {
    const payload = JSON.stringify({
        site: SEARCH_SITE_ID,
        search_id: searchId,
        document_id: documentId,
        position,
    });
    if (typeof navigator.sendBeacon === 'function') {
        const body = new Blob(
            [payload],
            { type: 'application/json' }
        );
        if (
            navigator.sendBeacon(
                SEARCH_CLICK_API_ENDPOINT,
                body
            )
        ) {
            return;
        }
    }

    void fetch(
        SEARCH_CLICK_API_ENDPOINT,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'same-origin',
            keepalive: true,
            body: payload,
        }
    ).catch(error => {
        console.error(
            'Search click tracking failed.',
            error
        );
    });
};

const setupSearchResultClickTracking = (
    container: HTMLElement
): void => {
    container.addEventListener(
        'click',
        event => {
            const target = event.target;
            if (!(target instanceof Element)) {
                return;
            }
            const link =
                target.closest<HTMLAnchorElement>(
                    '.p-search__list a[href]'
                );
            if (!link || !currentSearchQuery) {
                return;
            }
            const item =
                link.closest<HTMLElement>(
                    '.p-search__item'
                );
            const documentId =
                item?.dataset.documentId || '';
            const position = Number(
                item?.dataset.resultPosition || '0'
            );
            if (
                currentSearchId &&
                documentId &&
                position > 0
            ) {
                sendSearchPlatformClick(
                    currentSearchId,
                    documentId,
                    position
                );
            }
            sendSearchAnalyticsEvent(
                'search_result_click',
                currentSearchQuery,
                {
                    result_position: position,
                    result_url: link.href,
                }
            );
        }
    );
};

const setupSearchResultImageFallback = (
    container: HTMLElement
): void => {
    container.addEventListener(
        'error',
        event => {
            const target = event.target;
            if (
                !(target instanceof HTMLImageElement) ||
                !target.classList.contains('p-search__img') ||
                target.dataset.fallbackApplied === 'true'
            ) {
                return;
            }

            target.dataset.fallbackApplied = 'true';
            target.src = SEARCH_DEFAULT_THUMBNAIL_URL;
        },
        true
    );
};

const escapeHtml = (
    value: string
): string => {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
};

const truncateText = (
    value: string,
    maxLength: number
): string => {
    if (value.length <= maxLength) {
        return value;
    }
    return `${value.slice(0, maxLength)}${truncationSuffix}`;
};

const getSearchQuery = (): string => {
    const params = new URLSearchParams(
        window.location.search
    );
    return (params.get('q') || '').trim();
};

const setSearchFormValue = (
    query: string
): void => {
    const inputs =
        document.querySelectorAll<HTMLInputElement>(
            '#result-search-form input[name="q"], #header-search-form input[name="q"]'
        );
    inputs.forEach(input => {
        input.value = query;
    });
};

// PAGE_* 相当の値を一元生成
const buildSearchPageMeta = (
    query: string
): SearchPageMeta => {
    const encodedQuery =
        encodeURIComponent(query);
    const permalink =
        `${window.location.origin}/search/?q=${encodedQuery}`;
    return {
        PAGE_TITLE:
            `検索結果：「${query}」と一致するもの`,
        PAGE_DESCRIPTION:
            `「${query}」の検索結果です。KONOTEのサイト内から関連するコンテンツを検索できます。`,
        PAGE_KEYWORD:
            query,
        PAGE_CANONICAL:
            permalink,
        PAGE_PERMALINK:
            permalink,
    };
};

// URLの?q=で確定したページ情報だけ更新
// ライブ検索時には呼ばない
const updateSearchPageMeta = (
    query: string
): void => {
    if (!query) {
        return;
    }
    const meta =
        buildSearchPageMeta(query);
    // title
    document.title =
        meta.PAGE_TITLE;
    // description
    const description =
        document.querySelector<HTMLMetaElement>(
            'meta[name="description"]'
        );
    if (description) {
        description.content =
            meta.PAGE_DESCRIPTION;
    }
    // keywords
    const keywords =
        document.querySelector<HTMLMetaElement>(
            'meta[name="keywords"]'
        );
    if (keywords) {
        keywords.content =
            meta.PAGE_KEYWORD;
    }
    // canonical
    const canonical =
        document.querySelector<HTMLLinkElement>(
            'link[rel="canonical"]'
        );
    if (canonical) {
        canonical.href =
            meta.PAGE_CANONICAL;
    }
    // og:url
    const ogUrl =
        document.querySelector<HTMLMetaElement>(
            'meta[property="og:url"]'
        );
    if (ogUrl) {
        ogUrl.content =
            meta.PAGE_PERMALINK;
    }
    // og:title
    const ogTitle =
        document.querySelector<HTMLMetaElement>(
            'meta[property="og:title"]'
        );
    if (ogTitle) {
        ogTitle.content =
            meta.PAGE_TITLE;
    }
    // og:description
    const ogDescription =
        document.querySelector<HTMLMetaElement>(
            'meta[property="og:description"]'
        );
    if (ogDescription) {
        ogDescription.content =
            meta.PAGE_DESCRIPTION;
    }
    // twitter:title
    const twitterTitle =
        document.querySelector<HTMLMetaElement>(
            'meta[name="twitter:title"]'
        );
    if (twitterTitle) {
        twitterTitle.content =
            meta.PAGE_TITLE;
    }
    // twitter:description
    const twitterDescription =
        document.querySelector<HTMLMetaElement>(
            'meta[name="twitter:description"]'
        );
    if (twitterDescription) {
        twitterDescription.content =
            meta.PAGE_DESCRIPTION;
    }
    // page heading
    const searchHeading =
        document.querySelector<HTMLElement>(
            '[data-search-heading]'
        );
    if (searchHeading) {
        searchHeading.textContent =
            '検索結果';
    }
    // breadcrumb
    const breadcrumbResult =
        document.querySelector<HTMLElement>(
            '[data-search-breadcrumb-result]'
        );
    if (breadcrumbResult) {
        const breadcrumbTitle =
            breadcrumbResult.querySelector<HTMLElement>(
                '[data-search-breadcrumb-title]'
            );
        if (breadcrumbTitle) {
            breadcrumbTitle.textContent =
                meta.PAGE_TITLE;
        }
        breadcrumbResult.hidden = false;
    }
};

const renderMessage = (
    container: HTMLElement,
    message: string
): void => {
    container.innerHTML = `
        <span id="current-page" style="display: none;">1</span>
        <h2 id="page-title" class="search-results-header p-search__heading"
            ${escapeHtml(message)}
        </h2>
    `;
};

const renderSearchResultItems = (
    results: SearchResult[],
    startPosition = 0
): string => {
    return results
        .map((result, index) => {
            const resultPosition =
                startPosition + index + 1;
            const documentId = escapeHtml(
                result.document_id || ''
            );
            const sourceType = escapeHtml(
                result.source_type || ''
            );
            const title = escapeHtml(
                result.title || ''
            );
            const rawSummary =
                result.summary || '';
            const summary = escapeHtml(
                truncateText(
                    rawSummary,
                    summaryMaxLength
                )
            );
            const rawBody =
                result.body || '';
            const body = escapeHtml(
                truncateText(
                    rawBody,
                    bodyMaxLength
                )
            );
            const url = escapeHtml(
                result.url || '#'
            );
            const thumbnailUrl = escapeHtml(
                result.thumbnail_url ||
                SEARCH_DEFAULT_THUMBNAIL_URL
            );
            const categories = (
                result.categories || []
            ).map(category =>
                escapeHtml(category)
            );
            const tags = (
                result.tags || []
            ).map(tag =>
                escapeHtml(tag)
            );
            const score =
                typeof result.score === 'number'
                    ? result.score
                    : null;
            return `
                <li class="p-search__item"
                    data-document-id="${documentId}"
                    data-result-position="${resultPosition}"
                    data-source-type="${sourceType}"
                    data-score="${score ?? ''}">
                    <a href="${url}" class="p-search__txt" title="${title}">
                        <figure class="p-search__figure">
                            <img
                                src="${thumbnailUrl}"
                                alt="${title}"
                                class="p-search__img"
                                loading="lazy"
                                decoding="async"
                            >
                        </figure>

                    ${
                        showSourceType ||
                        showScore ||
                        showDocumentId
                            ? `
                                <p class="p-search__txt--meta">
                                    ${
                                        showSourceType
                                            ? `
                                                <span class="p-search__txt--source-type">
                                                    ${sourceType}
                                                </span>
                                            `
                                            : ''
                                    }
                                    ${
                                        showScore
                                            ? `
                                                <span class="p-search__txt--score">
                                                    score: ${score ?? ''}
                                                </span>
                                            `
                                            : ''
                                    }
                                    ${
                                        showDocumentId
                                            ? `
                                                <span class="p-search__txt--document-id">
                                                    ${documentId}
                                                </span>
                                            `
                                            : ''
                                    }
                                </p>
                            `
                            : ''
                    }
                        <p class="p-search__txt--title">
                        ${title}
                        </p>

                        ${
                            showSummary && summary
                                ? `
                                    <span class="p-search__txt--summary">
                                        ${summary}
                                    </span>
                                `
                                : ''
                        }
                        ${
                            showBody && body
                                ? `
                                    <span class="p-search__txt--body">
                                        ${body}
                                    </span>
                                `
                                : ''
                        }
                    ${
                        showCategories &&
                        categories.length > 0
                            ? `
                                <ul class="p-search__categories">
                                    ${categories
                                        .map(category => `
                                            <li class="p-search__txt--category">
                                                ${category}
                                            </li>
                                        `)
                                        .join('')}
                                </ul>
                            `
                            : ''
                    }
                    ${
                        showTags &&
                        tags.length > 0
                            ? `
                                <ul class="p-search__tags">
                                    ${tags
                                        .map(tag => `
                                            <li class="p-search__txt--tag">
                                                ${tag}
                                            </li>
                                        `)
                                        .join('')}
                                </ul>
                            `
                            : ''
                    }
                    </a>
                </li>
            `;
        })
        .join('');
};

const disconnectInfiniteScroll = (): void => {
    if (infiniteScrollObserver) {
        infiniteScrollObserver.disconnect();
        infiniteScrollObserver = null;
    }
};

const setLoadingMoreState = (
    container: HTMLElement,
    isLoading: boolean,
    message = '検索結果を追加で読み込んでいます…',
    isComplete = false
): void => {
    const status =
        container.querySelector<HTMLElement>(
            '[data-search-loading]'
        );

    if (!status) {
        return;
    }
    status.textContent = message;
    status.classList.toggle(
        'is-complete',
        isComplete
    );
    status.hidden = !isLoading;
};

const appendSearchResults = (
    container: HTMLElement,
    results: SearchResult[]
): void => {
    const list =
        container.querySelector<HTMLUListElement>(
            '.p-search__list'
        );

    if (!list) {
        return;
    }
    list.insertAdjacentHTML(
        'beforeend',
        renderSearchResultItems(
            results,
            currentSearchOffset
        )
    );
};

const fetchSearchResults = async (
    query: string,
    limit: number,
    offset: number,
    track: boolean,
    signal?: AbortSignal
): Promise<SearchResponse> => {
    const url = new URL(
        SEARCH_API_ENDPOINT,
        window.location.origin
    );
    url.searchParams.set(
        'q',
        query
    );
    url.searchParams.set(
        'site',
        SEARCH_SITE_ID
    );
    url.searchParams.set(
        'limit',
        String(limit)
    );
    url.searchParams.set(
        'offset',
        String(offset)
    );
    url.searchParams.set(
        'track',
        String(track)
    );
    const response = await fetch(
        url.toString(),
        {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
            credentials: 'same-origin',
            signal,
        }
    );
    if (!response.ok) {
        throw new Error(
            `Search API error: ${response.status}`
        );
    }
    return response.json() as Promise<SearchResponse>;
};

const loadMoreSearchResults = async (
    container: HTMLElement
): Promise<void> => {
    if (
        isLoadingMore ||
        !currentSearchQuery ||
        currentSearchOffset >= currentSearchTotal
    ) {
        return;
    }
    isLoadingMore = true;
    const loadingStartedAt = performance.now();
    setLoadingMoreState(
        container,
        true
    );
    const requestId = activeSearchRequestId;
    const query = currentSearchQuery;
    const offset = currentSearchOffset;
    const controller = new AbortController();
    let hasLoadMoreError = false;
    activeSearchAbortController = controller;
    try {
        const data = await fetchSearchResults(
            query,
            searchResultLimit,
            offset,
            false,
            controller.signal
        );
        if (
            requestId !== activeSearchRequestId ||
            query !== currentSearchQuery
        ) {
            return;
        }

        if (data.results.length === 0) {
            currentSearchOffset = currentSearchTotal;
            disconnectInfiniteScroll();
            return;
        }

        appendSearchResults(
            container,
            data.results
        );

        currentSearchOffset +=
            data.results.length;

        currentSearchTotal = data.total;

        if (
            currentSearchOffset >= currentSearchTotal
        ) {
            disconnectInfiniteScroll();
        }
    } catch (error) {
        if (
            error instanceof DOMException &&
            error.name === 'AbortError'
        ) {
            return;
        }

        console.error(error);

        hasLoadMoreError = true;

        disconnectInfiniteScroll();

        setLoadingMoreState(
            container,
            true,
            '追加の検索結果を取得できませんでした。'
        );

        return;
    } finally {
        if (
            requestId === activeSearchRequestId
        ) {
            const loadingElapsed =
                performance.now() - loadingStartedAt;
            const loadingRemaining =
                searchLoadingMinimumMs - loadingElapsed;

            if (loadingRemaining > 0) {
                await new Promise<void>(resolve => {
                    window.setTimeout(
                        resolve,
                        loadingRemaining
                    );
                });
            }

            if (
                requestId !== activeSearchRequestId
            ) {
                return;
            }

            isLoadingMore = false;

            if (!hasLoadMoreError) {
                if (
                    currentSearchOffset >= currentSearchTotal
                ) {
                    setLoadingMoreState(
                        container,
                        true,
                        `全${currentSearchTotal}件の検索結果を表示しました。`,
                        true
                    );
                } else {
                    setLoadingMoreState(
                        container,
                        false
                    );
                }
            }

            if (
                activeSearchAbortController === controller
            ) {
                activeSearchAbortController = null;
            }
        }
    }
};


const setupInfiniteScroll = (
    container: HTMLElement
): void => {
    disconnectInfiniteScroll();

    if (
        currentSearchOffset >= currentSearchTotal
    ) {
        return;
    }

    const sentinel =
        container.querySelector<HTMLElement>(
            '[data-search-sentinel]'
        );

    if (!sentinel) {
        return;
    }

    infiniteScrollObserver =
        new IntersectionObserver(
            entries => {
                if (
                    entries.some(entry =>
                        entry.isIntersecting
                    )
                ) {
                    void loadMoreSearchResults(
                        container
                    );
                }
            },
            {
                root: null,
                rootMargin:
                    `0px 0px ${infiniteScrollThreshold}px 0px`,
                threshold: 0,
            }
        );

    infiniteScrollObserver.observe(
        sentinel
    );
};


const renderSearchResults = (
    container: HTMLElement,
    data: SearchResponse
): void => {
    if (
        data.total === 0 ||
        data.results.length === 0
    ) {
        const suggestions =
            Array.isArray(data.suggestions)
                ? data.suggestions
                : [];
        const suggestionLinks = suggestions
            .map(suggestion => {
                const href =
                    `/search/?q=${encodeURIComponent(suggestion)}`;

                return `
                    <a
                        href="${href}"
                        class="p-search__suggestion"
                    >${escapeHtml(suggestion)}</a>
                `;
            })
            .join('、');

        container.innerHTML = `
            <span
                id="current-page"
                style="display: none;"
            >1</span>

            <h2
                id="page-title"
                class="search-results-header p-search__heading"
            >
                「${escapeHtml(data.query)}」と一致するものはありませんでした
            </h2>

            ${
                suggestionLinks
                    ? `
                        <p class="p-search__suggestions">
                            もしかして：${suggestionLinks}
                        </p>
                    `
                    : ''
            }
        `;

        return;
    }

    const items = renderSearchResultItems(
        data.results,
        0
    );

    container.innerHTML = `
        <span
            id="current-page"
            style="display: none;"
        >1</span>

        <h2
            id="page-title"
            class="search-results-header p-search__heading"
        >
            「${escapeHtml(data.query)}」と一致するもの
        </h2>

        ${
            showSearchStats
                ? `
                    <p class="p-search__txt--stats">
                        ${data.total.toLocaleString()}件 /
                        ${data.elapsed_ms.toFixed(3)} ms
                    </p>
                `
                : ''
        }

        <div
            class="search-results-container autopagerize_page_element"
        >
            <ul class="p-search__list">
                ${items}
            </ul>
        </div>

        <p
            class="p-search__loading"
            data-search-loading
            role="status"
            aria-live="polite"
            hidden
        ></p>

        <div
            class="p-search__sentinel"
            data-search-sentinel
            aria-hidden="true"
        ></div>
    `;

    currentSearchQuery = data.query;
    currentSearchId = data.search_id;
    currentSearchOffset = data.results.length;
    currentSearchTotal = data.total;

    setupInfiniteScroll(
        container
    );
};


const searchAndRender = async (
    query: string,
    container: HTMLElement,
    track: boolean
): Promise<void> => {
    activeSearchRequestId += 1;

    const requestId = activeSearchRequestId;

    if (activeSearchAbortController) {
        activeSearchAbortController.abort();
        activeSearchAbortController = null;
    }

    disconnectInfiniteScroll();

    currentSearchQuery = '';
    currentSearchId = null;
    currentSearchOffset = 0;
    currentSearchTotal = 0;
    isLoadingMore = false;

    if (!query) {
        renderMessage(
            container,
            '検索キーワードを入力してください。'
        );

        return;
    }

    renderMessage(
        container,
        '検索しています。'
    );

    const controller = new AbortController();

    activeSearchAbortController = controller;

    try {
        const data =
            await fetchSearchResults(
                query,
                searchResultLimit,
                0,
                track,
                controller.signal
            );

        if (
            requestId !== activeSearchRequestId
        ) {
            return;
        }

        if (track) {
            trackSearchResultsLoaded(data);
        }

        renderSearchResults(
            container,
            data
        );
    } catch (error) {
        if (
            error instanceof DOMException &&
            error.name === 'AbortError'
        ) {
            return;
        }

        console.error(error);

        renderMessage(
            container,
            '検索結果を取得できませんでした。時間をおいて再度お試しください。'
        );
    } finally {
        if (
            activeSearchAbortController === controller
        ) {
            activeSearchAbortController = null;
        }
    }
};


const handleLiveSearch = (
    input: HTMLInputElement,
    container: HTMLElement
): void => {
    window.clearTimeout(
        searchDebounceTimer
    );

    searchDebounceTimer =
        window.setTimeout(
            () => {
                const query =
                    input.value.trim();

                // ライブ検索では
                // URL / meta / breadcrumb は変更しない
                void searchAndRender(
                    query,
                    container,
                    false
                );
            },
            searchDebounceMs
        );
};


const initSearchPlatform = async (): Promise<void> => {
    const container =
        document.querySelector<HTMLElement>(
            '#search-platform-results'
        );

    if (!container) {
        return;
    }

    const query =
        getSearchQuery();

    setSearchFormValue(
        query
    );

    // URLの?q=で確定したページ情報のみ更新
    updateSearchPageMeta(
        query
    );

    await searchAndRender(
        query,
        container,
        true
    );
};


document.addEventListener(
    'DOMContentLoaded',
    () => {
        void initSearchPlatform();

        const container =
            document.querySelector<HTMLElement>(
                '#search-platform-results'
            );

        const input =
            document.querySelector<HTMLInputElement>(
                '#result-search-form input[name="q"]'
            );

        if (
            container &&
            input
        ) {
            setupSearchResultClickTracking(
                container
            );

            setupSearchResultImageFallback(
                container
            );

            input.addEventListener(
                'input',
                () => {
                    handleLiveSearch(
                        input,
                        container
                    );
                }
            );
        }
    }
);


// SP検索UI
const searchField =
    document.querySelector(
        '.js-seacher-sp'
    );

const backdrop =
    document.querySelector(
        '.js-backdrop'
    );


function openSearch() {
    searchField?.classList.add(
        'is-show'
    );

    backdrop?.classList.add(
        'is-show'
    );
}


function closeSearch() {
    searchField?.classList.remove(
        'is-show'
    );

    backdrop?.classList.remove(
        'is-show'
    );
}


window.addEventListener(
    'resize',
    () => {
        if (
            window.innerWidth >= 768
        ) {
            closeSearch();
        }
    }
);
